import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import UserPreferenceSingleton from './userPreferenceSingleton';
import { database } from '../utils/firebase';
import { captureException } from '../services/logService';
import { rootStore } from '../mobx/rootStore';

/**
 * Updates the user's general preferences in Fire store.
 * @param {any} data - The preference data to update. Will be merged with existing preferences.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 */
export const updateUserPreference = async (data: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id) return;
  const userPreferencesRef = doc(database, 'user_preferences_v2', userDetails?.id);
  try {
    await setDoc(userPreferencesRef, data, { merge: true });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Updates the user's workspace-specific preferences in Fire store.
 * @param {any} data - The workspace preference data to update. Will be merged with existing preferences.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 * @throws Will throw an error if user ID or workspace ID is missing.
 */
export const updateUserWorkspacePreference = async (data: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id) return;
  const userWorkspacePreferencesRef = doc(
    database,
    `user_preferences_v2/${userDetails?.id}/workspaces`,
    rootStore.workspaceStore.currentWorkspace.id
  );
  try {
    await setDoc(userWorkspacePreferencesRef, data, { merge: true });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Updates the user's project-specific preferences within a workspace in Fire store.
 * @param {string} projectId - The ID of the project to update preferences for.
 * @param {any} data - The project preference data to update. Will be merged with existing preferences.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 * @throws Will throw an error if user ID or workspace ID is missing.
 */
export const updateWorkspaceProjectPreference = async (projectId: string, data: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id || !projectId) return;
  const projectPreferencesRef = doc(
    database,
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace.id}/projects`,
    projectId
  );
  try {
    await setDoc(projectPreferencesRef, data, { merge: true });
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's general preferences in Fire store.
 * @param {any} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 */
export const getUserPreferenceData = async (field: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id) return undefined;
  const userPreferencesRef = doc(database, 'user_preferences_v2', userDetails?.id);
  try {
    const docSnapshot = await getDoc(userPreferencesRef);
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const fieldValue = data[field];
      return fieldValue;
    } else {
      return undefined;
    }
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's workspace preferences in Fire store.
 * @param {any} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 * @throws Will return undefined if user ID or workspace ID is missing.
 */
export const getUserWorkspacePreferenceData = async (field: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id) return undefined;
  const userPreferencesRef = doc(
    database,
    `user_preferences_v2/${userDetails?.id}/workspaces`,
    rootStore.workspaceStore.currentWorkspace?.id
  );
  try {
    const docSnapshot = await getDoc(userPreferencesRef);
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const fieldValue = data[field];
      return fieldValue;
    } else {
      return undefined;
    }
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's project preferences within a workspace in Fire store.
 * @param {string} projectId - The ID of the project to get preferences for.
 * @param {any} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 * @throws Will return undefined if user ID, workspace ID, or project ID is missing.
 */
export const getWorkspaceProjectPreferenceData = async (projectId: string, field: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id || !projectId) return undefined;
  const userPreferencesRef = doc(
    database,
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace?.id}/projects`,
    projectId
  );
  try {
    const docSnapshot = await getDoc(userPreferencesRef);
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const fieldValue = data[field];
      return fieldValue;
    } else {
      return undefined;
    }
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves all project preferences from the user's workspace in Fire store.
 * @returns {Promise<any>} A promise that resolves to the project preferences, or undefined if not found.
 * @throws Will return undefined if user ID or workspace ID is missing.
 */
export const getWorkspaceAllProjectsPreferenceData = async () => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id) return undefined;
  const userPreferencesRef = collection(
    database,
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace?.id}/projects`
  );
  try {
    const docSnapshot = await getDocs(userPreferencesRef);
    if (docSnapshot.size > 0) {
      const projectsDataMap: any = {}; // Remove any existing data
      docSnapshot.forEach((doc) => {
        if (doc.exists()) {
          projectsDataMap[doc.id] = doc.data();
        }
      });
      return projectsDataMap;
    } else {
      return undefined;
    }
  } catch (error) {
    captureException(error);
  }
};
